import { keyBy } from 'lodash'
import { useCallback, useMemo } from 'react'

import useIsAdmin from '~/auth/hooks/useIsAdmin'
import type { TimeRange } from '~/data/time/types'
import { useCurrentOrgId } from '~/store/slices/currentOrg'

import { useGetBootEventsQuery } from '../client/billing'
import type {
  ChargerDetailModel,
  ChargerModel,
  CmsChargerFaultsGetFaultsArgs,
  CmsTimespanGetHistoricalStatusesArgs,
  GetChargersArgs,
  GetChargingTransactionsArgs
} from '../client/chargers'
import {
  useCmsChargerFaultsGetFaultsQuery,
  useCmsTimespanGetHistoricalStatusesQuery,
  useCmsUtilizationGetUtilizationQuery,
  useGetChargerConnectorsQuery,
  useGetChargerQuery,
  useGetChargersQuery,
  useGetChargingTransactionsQuery,
  useGetConnectionEventsQuery
} from '../client/chargers'
import type { UsePollingOptions, UseQueryOptions } from './polling'
import { usePolling } from './polling'
import type { MaybeDateRange } from './types'
import { useCurrentUser } from './users'
import { combineSkip, useTransformedDict, useValidatedDateRange } from './utils'

/**
 * OCPP central-system `registrationStatus` values persisted on `charge_box`.
 * `Rejected` is the manual-quarantine ("SEVER websocket") state written by
 * `POST /api/chargers/{id}/modify-websocket-connection` with `connectionModifier: 'SEVER'`.
 */
export const REGISTRATION_STATUS = {
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected'
} as const

export type RegistrationStatus = (typeof REGISTRATION_STATUS)[keyof typeof REGISTRATION_STATUS]

export function isChargerQuarantined(
  charger: Pick<ChargerDetailModel | ChargerModel, 'registrationStatus'>
): boolean {
  return charger.registrationStatus === REGISTRATION_STATUS.REJECTED
}

export function useChargerSiteIdsFilter(siteIds?: string[]) {
  const { synopUser } = useCurrentUser()
  const isAdmin = useIsAdmin()
  return useMemo(() => {
    if (siteIds?.length) return siteIds
    if (isAdmin) return undefined
    if (!synopUser?.sites || synopUser.sites.length === 0) return undefined
    return synopUser.sites ?? undefined
  }, [synopUser, isAdmin, siteIds])
}

/******************************************************************************
 * GET /chargers
 ******************************************************************************/
export function usePolledChargers(args?: GetChargersArgs, options?: UsePollingOptions) {
  const { useCache, ...pollingOptions } = options ?? {}
  const siteIds = useChargerSiteIdsFilter(args?.siteIds)

  // This approach is to avoid running into the payload size limit of the API.
  // This is a temporary solution until we can use the API to properly paginate the data.
  const resultPage1 = usePolling(
    useGetChargersQuery,
    { page: 0, count: 1000, ...args, siteIds },
    combineSkip(false, pollingOptions)
  )

  const resultPage2 = usePolling(
    useGetChargersQuery,
    { page: 1, count: 1000, ...args, siteIds },
    combineSkip(false, pollingOptions)
  )

  const resultPage3 = usePolling(
    useGetChargersQuery,
    { page: 2, count: 1000, ...args, siteIds },
    combineSkip(false, pollingOptions)
  )

  const resultPage4 = usePolling(
    useGetChargersQuery,
    { page: 3, count: 1000, ...args, siteIds },
    combineSkip(false, pollingOptions)
  )

  const resultPage5 = usePolling(
    useGetChargersQuery,
    { page: 4, count: 1000, ...args, siteIds },
    combineSkip(false, pollingOptions)
  )

  const resultPage6 = usePolling(
    useGetChargersQuery,
    { page: 5, count: 1000, ...args, siteIds },
    combineSkip(false, pollingOptions)
  )

  const resultPage7 = usePolling(
    useGetChargersQuery,
    { page: 6, count: 1000, ...args, siteIds },
    combineSkip(false, pollingOptions)
  )

  const resultPage8 = usePolling(
    useGetChargersQuery,
    { page: 7, count: 1000, ...args, siteIds },
    combineSkip(false, pollingOptions)
  )

  // Prevent unnecessary re-renders of the chargers by waiting until all pages have been fetched
  const combinedChargers = useMemo(() => {
    if (
      resultPage1.isSuccess &&
      resultPage2.isSuccess &&
      resultPage3.isSuccess &&
      resultPage4.isSuccess &&
      resultPage5.isSuccess &&
      resultPage6.isSuccess &&
      resultPage7.isSuccess &&
      resultPage8.isSuccess
    ) {
      return [
        ...(resultPage1.data.items || []),
        ...(resultPage2.data.items || []),
        ...(resultPage3.data.items || []),
        ...(resultPage4.data.items || []),
        ...(resultPage5.data.items || []),
        ...(resultPage6.data.items || []),
        ...(resultPage7.data.items || []),
        ...(resultPage8.data.items || [])
      ]
    }
    return []
  }, [resultPage1, resultPage2, resultPage3, resultPage4, resultPage5, resultPage6, resultPage7, resultPage8])

  // Combine cached data from currentData (last successful result for each page)
  const cachedChargers = useMemo(() => {
    const allPages = [
      resultPage1,
      resultPage2,
      resultPage3,
      resultPage4,
      resultPage5,
      resultPage6,
      resultPage7,
      resultPage8
    ]
    return allPages.flatMap((page) => page.currentData?.items ?? [])
    // We only care about recomputing when the currentData changes, not the whole query result object.
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    resultPage1.currentData,
    resultPage2.currentData,
    resultPage3.currentData,
    resultPage4.currentData,
    resultPage5.currentData,
    resultPage6.currentData,
    resultPage7.currentData,
    resultPage8.currentData
  ])

  const refetch = useCallback(() => {
    resultPage1.refetch()
    resultPage2.refetch()
    resultPage3.refetch()
    resultPage4.refetch()
    resultPage5.refetch()
    resultPage6.refetch()
    resultPage7.refetch()
    resultPage8.refetch()
  }, [resultPage1, resultPage2, resultPage3, resultPage4, resultPage5, resultPage6, resultPage7, resultPage8])

  const pages = [resultPage1, resultPage2, resultPage3, resultPage4, resultPage5, resultPage6, resultPage7, resultPage8]
  const isLoading = pages.some((page) => page.isLoading)
  const isSuccess = pages.every((page) => page.isSuccess)
  const isFetching = pages.some((page) => page.isFetching)
  const isError = pages.some((page) => page.isError)
  const totalItems = resultPage1.data?.totalItems ?? 0
  const cachedTotalItems = resultPage1.currentData?.totalItems ?? 0

  // When useCache is true, prefer cached data.
  // Cached reference will update as fresh data loads.
  const effectiveChargers = useCache && cachedChargers.length > 0 ? cachedChargers : combinedChargers
  const effectiveTotalItems = useCache && cachedChargers.length > 0 ? cachedTotalItems : totalItems

  // When useCache is true, only show loading if there's no cached data
  const effectiveIsLoading = useCache ? isLoading && cachedChargers.length === 0 : isLoading

  return {
    chargers: { items: effectiveChargers, totalItems: effectiveTotalItems },
    isLoading: effectiveIsLoading,
    isSuccess,
    isError,
    refetch,
    isFetching
  }
}

export function useChargers(args?: GetChargersArgs, options?: UseQueryOptions) {
  return usePolledChargers(args, { ...options, intervalSeconds: 0 })
}

export function usePolledChargersAtSite(
  siteId?: string,
  args?: Omit<GetChargersArgs, 'siteIds'>,
  options?: UsePollingOptions
) {
  return usePolledChargers({ siteIds: siteId ? [siteId] : [], ...args }, combineSkip(!siteId, options))
}

export function useChargersAtSite(siteId?: string, args?: Omit<GetChargersArgs, 'siteIds'>, options?: UseQueryOptions) {
  return usePolledChargersAtSite(siteId, args, { ...options, intervalSeconds: 0 })
}

export function usePolledChargersForOrg(
  orgId: string,
  args?: Omit<GetChargersArgs, 'organizationId'>,
  options?: UsePollingOptions
) {
  return usePolledChargers({ organizationId: [orgId], ...args }, combineSkip(!orgId, options))
}

export function useChargersForOrg(
  orgId: string,
  args?: Omit<GetChargersArgs, 'organizationId'>,
  options?: UseQueryOptions
) {
  return usePolledChargersForOrg(orgId, args, { ...options, intervalSeconds: 0 })
}

export function useChargersForCurrentOrg(args?: Omit<GetChargersArgs, 'organizationId'>, options?: UseQueryOptions) {
  const currentOrgId = useCurrentOrgId()
  return useChargersForOrg(currentOrgId, args, options)
}

export function useOrgChargersMap(
  orgId?: string,
  args?: Omit<GetChargersArgs, 'organizationId'>,
  options?: UsePollingOptions
) {
  const currentOrgId = useCurrentOrgId()
  const { isSuccess, isLoading, chargers } = usePolledChargersForOrg(orgId ?? currentOrgId, args, options)
  const chargerMap = useMemo(() => keyBy(chargers?.items, 'chargerId'), [chargers])
  const orgChargers = useTransformedDict(chargerMap, { caseInsensitive: true })
  const lookup = useCallback((chargerId?: string) => orgChargers[chargerId ?? ''], [orgChargers])

  return {
    orgChargers,
    isLoading,
    isSuccess,
    lookup
  }
}

/******************************************************************************
 * GET /chargers/{chargerId}
 ******************************************************************************/
export function usePolledCharger(chargerId = '', options?: UsePollingOptions) {
  const result = usePolling(useGetChargerQuery, { chargerId }, combineSkip(!chargerId, options))

  // Ensure the charger returned from the hook matches the ID passed in. This is needed when the ID
  // changes or is cleared.
  const charger = result.data?.chargerId === chargerId ? result.data : undefined
  return { charger, ...result }
}

export function useCharger(chargerId?: string, options?: UseQueryOptions) {
  return usePolledCharger(chargerId, { ...options, intervalSeconds: 0 })
}

/******************************************************************************
 * GET /chargers/{chargerId}/connectors
 ******************************************************************************/
export function usePolledConnectors(chargerId: string = '', options?: UsePollingOptions) {
  const result = usePolling(useGetChargerConnectorsQuery, { chargerId }, combineSkip(!chargerId, options))
  return { ...result, connectors: result.data }
}

export function useConnectors(chargerId?: string) {
  return usePolledConnectors(chargerId, { intervalSeconds: 0 })
}

/******************************************************************************
 * GET /chargers/faults
 ******************************************************************************/
export type ChargerFaultsArgs = Omit<CmsChargerFaultsGetFaultsArgs, 'occurredAfter' | 'occurredBefore'>
export function usePolledChargerFaults(
  args?: ChargerFaultsArgs,
  dateRange?: MaybeDateRange,
  options?: UsePollingOptions
) {
  const { from, to, range } = useValidatedDateRange(dateRange, { maxDays: 31 })
  const siteIds = useChargerSiteIdsFilter(args?.siteIds)
  return usePolling(
    useCmsChargerFaultsGetFaultsQuery,
    { page: 0, count: 1000, occurredAfter: from.time, occurredBefore: to.time, ...args, siteIds },
    combineSkip(!range.valid, options)
  )
}

export function useChargerFaults(args?: ChargerFaultsArgs, dateRange?: MaybeDateRange) {
  return usePolledChargerFaults(args, dateRange, { intervalSeconds: 0 })
}

/******************************************************************************
 * GET /chargers/timespan/statuses
 ******************************************************************************/
export type HistoricalStatusesArgs = Omit<CmsTimespanGetHistoricalStatusesArgs, 'fromTs' | 'toTs'>

/**
 * Longest range `GET /chargers/timespan/*` accepts (chargers-api `synop.max-timespan-range-in-days`).
 * Anything wider, or a range whose `fromTs` is after `toTs`, is a 400 — so it must never be polled.
 */
export const TIMESPAN_MAX_RANGE_DAYS = 366

export function usePolledHistoricalStatuses(
  args?: HistoricalStatusesArgs,
  dateRange?: MaybeDateRange,
  options?: UsePollingOptions
) {
  const { from, to, range } = useValidatedDateRange(dateRange, { maxDays: TIMESPAN_MAX_RANGE_DAYS })
  const siteIds = useChargerSiteIdsFilter(args?.siteIds)
  const result = usePolling(
    useCmsTimespanGetHistoricalStatusesQuery,
    { fromTs: from.time, toTs: to.time, ...args, siteIds },
    combineSkip(!range.valid, options)
  )

  return { statusSummary: result.data, ...result }
}

export function useHistoricalStatuses(args?: HistoricalStatusesArgs, dateRange?: MaybeDateRange) {
  return usePolledHistoricalStatuses(args, dateRange, { intervalSeconds: 0 })
}

/******************************************************************************
 * GET /transactions
 ******************************************************************************/
type ChargingTransactionsArgs = Omit<GetChargingTransactionsArgs, 'chargerId'>
export function usePolledChargingTransactions(
  chargerId: string = '',
  args?: ChargingTransactionsArgs,
  options?: UsePollingOptions
) {
  const result = usePolling(useGetChargingTransactionsQuery, { chargerId, ...args }, combineSkip(!chargerId, options))
  return { ...result, transactions: result.data }
}

/**
 * Polls for charging transaction data on a 5 second basis.
 */
export function usePolledTransactions(args: GetChargingTransactionsArgs, timeRange: TimeRange) {
  const { from, to } = useValidatedDateRange(timeRange)
  const result = usePolling(
    useGetChargingTransactionsQuery,
    { count: 1000, startTimeTo: to.time, stopTimeFrom: from.time, status: ['inProgress', 'ended'], ...args },
    { skip: !to.valid || !from.valid, intervalSeconds: 5 }
  )

  return { transactions: result.data, ...result }
}

/******************************************************************************
 * GET /charger-boot-events
 ******************************************************************************/
export type BootEventsArgs = {
  chargerId?: string
  siteId?: string[]
  includeRawEvents?: boolean
  bucket?: 'FIFTEEN_MINUTES' | 'THIRTY_MINUTES' | 'SIXTY_MINUTES'
}

export function usePolledBootEvents(args: BootEventsArgs, timeRange: TimeRange, options?: UsePollingOptions) {
  const { from, to } = useValidatedDateRange(timeRange)

  return usePolling(
    useGetBootEventsQuery,
    {
      ...args,
      includeRawEvents: args.includeRawEvents ?? false,
      from: from.time,
      to: to.time,
      bucket: args.bucket
    },
    combineSkip(!to.valid || !from.valid || !(args.chargerId || args.siteId?.length), options)
  )
}

export function useChargerBootEvents(args: BootEventsArgs, timeRange: TimeRange) {
  return usePolledBootEvents(args, timeRange, { intervalSeconds: 0 })
}

/******************************************************************************
 * GET /charger-connection-events
 ******************************************************************************/
export type ChargerConnectionEventsArgs = {
  chargerIds?: string[]
  siteId?: string
  nodeId?: string
  organizationId?: string
  latestOnly?: boolean
}

export function usePolledChargerConnectionEvents(
  args: ChargerConnectionEventsArgs,
  timeRange: TimeRange,
  options?: UsePollingOptions
) {
  const { from, to, range } = useValidatedDateRange(timeRange, { maxDays: 14 })

  return usePolling(
    useGetConnectionEventsQuery,
    {
      page: 0,
      count: 2000,
      ...args,
      from: from.time,
      to: to.time
    },
    combineSkip(!range.valid || !(args.siteId || args.nodeId || args.organizationId), options)
  )
}

export function useChargerConnectionEvents(args: ChargerConnectionEventsArgs, timeRange: TimeRange) {
  return usePolledChargerConnectionEvents(args, timeRange, { intervalSeconds: 0 })
}

/******************************************************************************
 * GET /chargers/utilization
 ******************************************************************************/
type PowerUtilizationOptions = {
  siteId?: string
  nodeIds?: string[]
}

/**
 * Polls for live power utilization at a site or node. Default polling interval is 5 seconds.
 */
export function usePolledLiveUtilization({ siteId, nodeIds }: PowerUtilizationOptions, options?: UsePollingOptions) {
  const result = usePolling(
    useCmsUtilizationGetUtilizationQuery,
    { siteIds: siteId ? [siteId] : undefined, nodeIds },
    combineSkip(!siteId && !nodeIds?.length, { intervalSeconds: 5, ...options })
  )

  return { utilization: result.data, ...result }
}
